import NoSleep from 'nosleep.js'
import platform from './platform'

/**
 * We're currently using the [NoSleep.js lib](https://github.com/richtr/NoSleep.js) to ease the WakeLock usage
 * in browsers, and the Insomnia plugin from Cordova to achieve the same in native apps.
 *
 * NoSleep.js used to do a bunch of complicated stuff, but nowadays (2021) it uses either the native WakeLock API from
 * modern browsers, something that doesn't really work (for us at least) in old iOS versions, and a hidden looping video
 * for other scenarios (which doesn't seem reliable either).
 *
 * Since WakeLock is supported by Chrome 84+ (which includes mobile variants and Edge), we're leaving Firefox and Safari
 * out; Safari mobile users can just get the app, Firefox users can just use Chrome/Edge, and Mac Safari users... they
 * just don't exist for our scenarios.
 */
export default class WakeLock {

	constructor() {
		if (!window.caffeine) {
			window.caffeine = new NoSleep()
		}
	}

	/**
	 * Won't work for anything that doesn't have the WakeLock API or is Native - NoSleep seems to not work on Firefox or
	 * work weirdly on Safari.
	 * @TODO: we could probably drop the library and write our own WakeLock code, as right now the video hack doesn't seem to work on Firefox or works horribly on Safari (see ch369)
	 * @TODO: revisit every now and then https://caniuse.com/wake-lock to check if we can ditch the Cordova plugin
	 * @param enable {boolean}
	 * @see wakeLockWorks
	 */
	toggle(enable) {
		if ('wakeLock' in navigator) {
			const maybePromise = (enable? window.caffeine.enable() : window.caffeine.disable())
			if (maybePromise) { //it seems in some(?) scenarios, nothing is returned from the NoSleep calls?
				maybePromise.catch((e) => {
					//possible errors: NotAllowedError - "the requesting page is not visible" (Android) / "document is hidden" (iOS)
					if (e.name == 'NotAllowedError' && (e.message.includes('not visible') || e.message.includes('is hidden'))) {
						//it's safe to ignore since there's no way to wakelock with the app in background
						console.debug('Skipping wakelock since app is in the background')
					} else {
						console.warn('Native WakeLock failed =>', e) //this effectively shows as a warning among Sentry stacktraces
					}
				})
			}
		} else if (platform.isNative()) {
			enable? window.plugins.insomnia.keepAwake() : window.plugins.insomnia.allowSleepAgain()
		}
	}

	static works() {
		return 'wakeLock' in navigator || platform.isNative()
	}
}
